import React, { useState, useEffect } from 'react'
import { useTheme, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { IoMdCheckmarkCircleOutline } from 'react-icons/io'
import { TiWarningOutline } from 'react-icons/ti'
import { TbCertificate, TbCertificateOff } from 'react-icons/tb'
import { FaFileArrowUp } from 'react-icons/fa6'
import { FaImage } from 'react-icons/fa'
import { uiDomainInitial, uiCertificateInitial } from '../_common/userInterfaceConstants'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import {
  checkCertificateValidity,
  checkCertificateMatch,
  checkFullChain,
  getCertificateExpireDate,
  getDomainFromCertificate,
} from '../_helpers/rsaCertificates'
import constants from '../../../config/constants'
import defaultTexts from '../../../texts/texts'
import useWindowDimensions from '../../_common/WindowDimesnions'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import CustomSwitchField from '../../_common/_form_fields/CustomSwitchField'
import CustomDialog from '../../_common/CustomDialog'
import CustomColorPicker from '../../_common/CustomColorPicker'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

const CustomerUiEdit = (props) => {
  const {
    customerUiDomain,
    setCustomerUiDomain,
    customerUiCertificates,
    setCustomerUiCertificates,
    customerUiSiteKey,
    setCustomerUiSiteKey,
    customerUiSecretKey,
    setCustomerUiSecretKey,
    customerUiLogo,
    setCustomerUiLogo,
    customerUiPrimaryColor,
    setCustomerUiPrimaryColor,
    customerUiSecondaryColor,
    setCustomerUiSecondaryColor,
    defaultOpenStackDomain,
    setDefaultOpenStackDomain,
    defaultOpenStackUserRole,
    setDefaultOpenStackUserRole,
    signUpEnabled,
    setSignUpEnabled,
    isUsername,
    setIsUsername,
    usernamePriority,
    setUsernamePriority,
    isPassword,
    setIsPassword,
    passwordPriority,
    setPasswordPriority,
    isConfirmPassword,
    setIsConfirmPassword,
    confirmPasswordPriority,
    setConfirmPasswordPriority,
    isEmail,
    setIsEmail,
    emailPriority,
    setEmailPriority,
    isTermsOfUse,
    setIsTermsOfUse,
    termsOfUseText,
    setTermsOfUseText,
    termsOfUseFile,
    setTermsOfUseFile,
    termsOfUseUrlPrefix,
    setTermsOfUseUrlPrefix,
    termsOfUseUrl,
    setTermsOfUseUrl,
    termsOfUseType,
    setTermsOfUseType,
    isPrivacyPolicy,
    setIsPrivacyPolicy,
    privacyPolicyText,
    setPrivacyPolicyText,
    privacyPolicyFile,
    setPrivacyPolicyFile,
    privacyPolicyUrlPrefix,
    setPrivacyPolicyUrlPrefix,
    privacyPolicyUrl,
    setPrivacyPolicyUrl,
    privacyPolicyType,
    setPrivacyPolicyType,
    customFields,
    setCustomFields,
    backgroundType,
    setBackgroundType,
    colorType,
    setColorType,
    backgroundColor,
    setBackgroundColor,
    gradientColor1,
    setGradientColor1,
    gradientColor2,
    setGradientColor2,
    backgroundImage,
    setBackgroundImage,
    headingText,
    setHeadingText,
    promoText1,
    setPromoText1,
    promoText2,
    setPromoText2,
    promoText3,
    setPromoText3,
    isContact,
    setIsContact,
    contactCompany,
    setContactCompany,
    contactPhone,
    setContactPhone,
    contactEmail,
    setContactEmail,
    certificateAction,
    setCertificateAction,
    domainsWithCertififcates,
    backgroundTypeList,
    colorTypeList,
    verifyDomain,
    errors,
    validateFormFields,
  } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()
  
  const customFieldInitial = {
    field_key: '',
    label: '',
    priority: '',
    required: false,
  }

  const [helperText, setHelperText] = useState('')
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [primaryColorDialogOpen, setPrimaryColorDialogOpen] = useState(false)
  const [secondaryColorDialogOpen, setSecondaryColorDialogOpen] = useState(false)
  const [backgroundColorDialogOpen, setBackgroundColorDialogOpen] = useState(false)
  const [gradientColor1DialogOpen, setGreadientColor1DialogOpen] = useState(false)
  const [gradientColor2DialogOpen, setGreadientColor2DialogOpen] = useState(false)
  const [anyDomainFilled, setAnyDomainFilled] = useState(false)

  const urlPrefixList = [
    {
      keyword: 'https://',
      value: 'https://',
    },
    {
      keyword: 'http://',
      value: 'http://',
    },
  ]

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const sectionLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    fontWeight: '500',
  }

  const appearanceLabelStyle = {
    color: theme.palette.vodka,
    fontSize: '20px',
    fontWeight: '500',
    textAlign: 'center',
    paddingBottom: '20px',
    marginTop: width < constants.lgBreakpoint ? '20px' : '0px',
  }

  const uploadButtonStyle = {
    width: '90px',
    height: '90px',
    borderRadius: '45px',
    color: 'white',
    background: theme.palette.grayX11,
    fontSize: '20px',
    fontWeight: '500',
  }

  const uploadLogoDescriptionStyle = {
    color: theme.palette.greenSheen,
    fontSize: '18px',
    fontWeight: '500',
    width: '30%',
  }

  const pickColorStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '30px',
    cursor: 'pointer',
  }

  const domainPeriodStyle = {
    color: theme.palette.violetsAreBlue,
    fontSize: '32px',
    fontWeight: '500',
    padding: '0px 5px',
  }

  const verifyButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const domainVerifyHelperStyle = {
    background: theme.palette.aliceBlue,
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
  }

  const fileMenuStyle = {
    color: theme.palette.grayX11,
    fontSize: '16px',
    fontWeight: '500',
    margin: '0px 0px 10px 110px',
  }

  const openFileStyle = {
    color: theme.palette.grayX11,
    fontSize: '12px',
    marginBottom: '5px',
    cursor: 'pointer',
    textDecoration: 'underline',

    '&:hover': {
      textDecoration: 'none',
    }
  }

  const fileMenuLinkStyle = {
    color: theme.palette.grayX11,
    cursor: 'pointer',

    '&:hover': {
      textDecoration: 'underline',
    }
  }

  const fileMenuActiveStyle = {
    color: theme.palette.vodka,
  }

  const fileUploadButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '500',
    fontSize: '18px',
    color: theme.palette.grayX11,
  }

  const requiredSwitchLabelStyle = {
    color: theme.palette.coralRed,
    fontSize: '16px',
  }

  const colorPickerIconSmallStyle = {
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    cursor: 'pointer',
  }

  const imageUploadButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '500',
    fontSize: '16px',
    color: theme.palette.oxfordBlue,
  }

  const rsaSwitchStyle = {
    height: '30px',
    padding: '0px 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'white',
    fontSize: '16px',
    fontWeight: '500',
  }

  const valueMdStyle = {
    color: theme.palette.greenSheen,
    fontSize: '16px',
    fontWeight: '500',
  }

  const getFileName = (file) => {
    const file_splitted = file.split('/')
    return file_splitted[file_splitted.length - 1]
  }

  const getError = (field_key) => {
    const error_object = errors.find(item => item.field_key === field_key)
    return error_object ? true : false
  }

  const handleOpenFile = (file) => {
    window.open(constants.s3URL + constants.s3Bucket + file, '_blank')
  }

  const validateCertificates = (certificates) => {
    let updated_certs = []

    certificates.forEach(item => {
      let cert_checked = false
      let cert_match = false
      let chain_checked = true
      let expire_date = null
      let domain = null

      if (item.certificate) {
        cert_checked = checkCertificateValidity(item.certificate)
      }

      if (cert_checked && item.private_key) {
        cert_match = checkCertificateMatch(item.certificate, item.private_key)
      }

      if (item.full_chain) {
        chain_checked = checkFullChain(item.full_chain)
      }

      if (cert_checked && cert_match && chain_checked) {
        expire_date = getCertificateExpireDate(item.certificate)
        domain = getDomainFromCertificate(item.certificate)
      }

      updated_certs.push({ ...item, expire_date, domain })
    })

    return updated_certs
  }

  const isDomainWithCertificate = (subdomain_prefix, domain_name) => {
    const domain = subdomain_prefix ? subdomain_prefix + '.' + domain_name : domain_name
    return domainsWithCertififcates.includes(domain)
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handlePrimaryColorDialogOpen = () => {
    setPrimaryColorDialogOpen(true)
  }

  const handlePrimaryColorDialogClose = () => {
    setPrimaryColorDialogOpen(false)
  }

  const handleSecondaryColorDialogOpen = () => {
    setSecondaryColorDialogOpen(true)
  }

  const handleSecondaryColorDialogClose = () => {
    setSecondaryColorDialogOpen(false)
  }

  const handleBackgroundColorDialogOpen = () => {
    setBackgroundColorDialogOpen(true)
  }

  const handleBackgroundColorDialogClose = () => {
    setBackgroundColorDialogOpen(false)
  }

  const handleGradientColor1DialogOpen = () => {
    setGreadientColor1DialogOpen(true)
  }

  const handleGradientColor1DialogClose = () => {
    setGreadientColor1DialogOpen(false)
  }

  const handleGradientColor2DialogOpen = () => {
    setGreadientColor2DialogOpen(true)
  }

  const handleGradientColor2DialogClose = () => {
    setGreadientColor2DialogOpen(false)
  }

  const handleAddDomain = () => {
    setCustomerUiDomain(prev => [...prev, { ...uiDomainInitial }])
  }

  const handleRemoveDomain = (thisIndex) => {
    setCustomerUiDomain(prev => prev.filter((item, index) => index !== thisIndex))
  }

  const handleChangeDomain = (event, field_key, thisIndex) => {
    const value = event.target.value
    
    setCustomerUiDomain(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push({ ...item, [field_key]: value, status: '' })
        } else {
          new_data.push(item)
        }
      })
      return new_data
    })

    validateFormFields()
  }

  const handleAddCertificate = () => {
    setCustomerUiCertificates(prev => [...prev, { ...uiCertificateInitial }])
  }

  const handleRemoveCertificate = () => {
    setCustomerUiCertificates(prev => prev.filter((item, index) => index !== prev.length - 1))
  }

  const handleChangeCertificates = (event, field_key, thisIndex) => {
    const value = event.target.value
    
    setCustomerUiCertificates(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push({ ...item, [field_key]: value })
        } else {
          new_data.push(item)
        }
      })
      new_data = validateCertificates(new_data)
      return new_data
    })

    validateFormFields()
  }

  const handleChangeSiteKey = (e) => {
    const value = e.target.value
    setCustomerUiSiteKey(value)
    validateFormFields()
  }

  const handleChangeSecretKey = (e) => {
    const value = e.target.value
    setCustomerUiSecretKey(value)
    validateFormFields()
  }

  const handleChangeDefaultOpenStackDomain = (e) => {
    const value = e.target.value
    setDefaultOpenStackDomain(value)
    validateFormFields()
  }

  const handleChangeDefaultOpenStackUserRole = (e) => {
    const value = e.target.value
    setDefaultOpenStackUserRole(value)
    validateFormFields()
  }

  const handleChangeSignUpEnabled = (value) => {
    setSignUpEnabled(value)
  }

  const handleChangeIsUsername = (value) => {
    setIsUsername(value)
  }

  const handleChangeUsernamePriority = (e) => {
    const value = e.target.value
    setUsernamePriority(value)
    validateFormFields()
  }

  const handleChangeIsPassword = (value) => {
    setIsPassword(value)
  }

  const handleChangePasswordPriority = (e) => {
    const value = e.target.value
    setPasswordPriority(value)
    validateFormFields()
  }

  const handleChangeIsConfirmPassword = (value) => {
    setIsConfirmPassword(value)
  }

  const handleChangeConfirmPasswordPriority = (e) => {
    const value = e.target.value
    setConfirmPasswordPriority(value)
    validateFormFields()
  }

  const handleChangeIsEmail = (value) => {
    setIsEmail(value)
  }

  const handleChangeEmailPriority = (e) => {
    const value = e.target.value
    setEmailPriority(value)
    validateFormFields()
  }

  const handleChangeIsTermsOfUse = (value) => {
    setIsTermsOfUse(value)
  }

  const handleChangeTermsOfUseText = (e) => {
    const value = e.target.value
    setTermsOfUseText(value)
    validateFormFields()
  }

  const handleUploadTermsOfUseFile = (event) => {
    setTermsOfUseFile(event.target.files[0])
    validateFormFields()
  }

  const handleChangeTermsOfUseUrlPrefix = (value) => {
    setTermsOfUseUrlPrefix(value)
  }

  const handleChangeTermsOfUseUrl = (e) => {
    const value = e.target.value
    setTermsOfUseUrl(value)
    validateFormFields()
  }

  const handleChangeIsPrivacyPolicy = (value) => {
    setIsPrivacyPolicy(value)
  }

  const handleChangePrivacyPolicyText = (e) => {
    const value = e.target.value
    setPrivacyPolicyText(value)
    validateFormFields()
  }

  const handleUploadPrivacyPolicyFile = (event) => {
    setPrivacyPolicyFile(event.target.files[0])
    validateFormFields()
  }

  const handleChangePrivacyPolicyUrlPrefix = (value) => {
    setPrivacyPolicyUrlPrefix(value)
  }

  const handleChangePrivacyPolicyUrl = (e) => {
    const value = e.target.value
    setPrivacyPolicyUrl(value)
    validateFormFields()
  }

  const handleAddCustomField = () => {
    setCustomFields(prev => [...prev, { ...customFieldInitial }])
  }

  const handleRemoveCustomField = () => {
    setCustomFields(prev => prev.filter((item, index) => index !== prev.length - 1))
  }

  const handleChangeCustomFields = (e, field_key, thisIndex) => {
    const value = field_key === 'required' ? e.target.checked : e.target.value
    
    setCustomFields(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push({ ...item, [field_key]: field_key === 'priority' ? parseInt(value) : value })
        } else {
          new_data.push(item)
        }
      })
      return new_data
    })

    validateFormFields()
  }

  const handleUploadLogo = (event) => {
    setCustomerUiLogo(event.target.files[0])
  }
  
  const handleChangeBackgroundType = (value) => {
    setBackgroundType(value)
    validateFormFields()
  }

  const handleChangeColorType = (value) => {
    setColorType(value)
    validateFormFields()
  }

  const handleChangeBackgroundColor = (e) => {
    const value = e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value
    setBackgroundColor(value)
    validateFormFields()
  }

  const handleChangeGradientColor1 = (e) => {
    const value = e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value
    setGradientColor1(value)
    validateFormFields()
  }

  const handleChangeGradientColor2 = (e) => {
    const value = e.target.value.startsWith('#') ? e.target.value : '#' + e.target.value
    setGradientColor2(value)
    validateFormFields()
  }

  const handleUploadBackgroundImageFile = (event) => {
    setBackgroundImage(event.target.files[0])
    validateFormFields()
  }

  const handleChangeHeadingText = (e) => {
    const value = e.target.value
    setHeadingText(value)
  }

  const handleChangePromoText1 = (e) => {
    const value = e.target.value
    setPromoText1(value)
  }

  const handleChangePromoText2 = (e) => {
    const value = e.target.value
    setPromoText2(value)
  }

  const handleChangePromoText3 = (e) => {
    const value = e.target.value
    setPromoText3(value)
  }

  const handleChangeIsContact = (value) => {
    setIsContact(value)
  }

  const handleChangeContactCompany = (e) => {
    const value = e.target.value
    setContactCompany(value)
    validateFormFields()
  }

  const handleAddContactPhone = () => {
    setContactPhone(prev => [...prev, ''])
  }

  const handleRemoveContactPhone = (thisIndex) => {
    setContactPhone(prev => prev.filter((item, index) => index !== thisIndex))
  }

  const handleChangeContactPhone = (e, field_key, thisIndex) => {
    const value = e.target.value

    setContactPhone(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push(value)
        } else {
          new_data.push(item)
        }
      })
      return new_data
    })

    validateFormFields()
  }

  const handleAddContactEmail = () => {
    setContactEmail(prev => [...prev, ''])
  }

  const handleRemoveContactEmail = (thisIndex) => {
    setContactEmail(prev => prev.filter((item, index) => index !== thisIndex))
  }

  const handleChangeContactEmail = (e, field_key, thisIndex) => {
    const value = e.target.value
    
    setContactEmail(prev => {
      let new_data = []
      prev.forEach((item, index) => {
        if (index === thisIndex) {
          new_data.push(value)
        } else {
          new_data.push(item)
        }
      })
      return new_data
    })

    validateFormFields()
  }

  const handleChangeCertificateAction = (action) => {
    setCertificateAction(prev => prev === action ? '' : action)
  }

  useEffect(() => {
    const domain_object = customerUiDomain.find(item => item.domain_name !== '')
    setAnyDomainFilled(domain_object ? true : false)
  }, [customerUiDomain])

  const getDomainVerifyHelper = () => {
    return (
      <Box
        sx={{
          ...domainVerifyHelperStyle,
          marginTop: '17px',
          color: anyDomainFilled ? theme.palette.darkCyan : theme.palette.coralRed,
          borderColor: anyDomainFilled ? theme.palette.darkCyan : theme.palette.coralRed,
        }}
      >
        {
          anyDomainFilled ?
          <>
            {
              customerUiDomain.map((item, index) => (
                <Box key={index} sx={{ marginBottom: '10px' }}>
                  {
                    item.domain_name &&
                    <>
                      <Box>{defaultTexts.type}: { item.subdomain_prefix ? 'CNAME' : 'A' }</Box>
                      <Box>{defaultTexts.name}: { item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name }</Box>
                      <Box>{defaultTexts.value}: { item.subdomain_prefix ? constants.customerUiCNameRecord + '.' : constants.customerUiARecord }</Box>
                      <Divider />
                    </>
                  }                          
                </Box>
              ))
            }
          </> :
          <>{defaultTexts.domainVerifyHelperText}</>
        }
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ ...fieldBoxStyle, paddingBottom: '0px' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={{ width: '60px' }}>
            <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.userInterfaceCustomerDomainHelperText)}>
              <HelpOutlineIcon fontSize='large' />
            </IconButton>
          </Box>
          <Box sx={sectionLabelStyle}>{defaultTexts.customerUiDomain}</Box>
        </Stack>
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Grid container>
          <Grid size={{ lg: 9 }}>

            <Box>
              <Grid container>
                <Grid size={{ xs: 12, sm: 9 }}>
                  {
                    customerUiDomain.map((item, index) => (
                      <Box key={index} sx={fieldBoxStyle}>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-start'
                        >
                          <Tooltip
                            title={ item.status ? capitalizeFirstLetter(item.status) : defaultTexts.notVerified }
                          >
                            <IconButton>
                              {
                                item.status === 'verified' ?
                                <IoMdCheckmarkCircleOutline size={32} color={theme.palette.mayGreen} /> :
                                <>
                                  {
                                    !item.status ?
                                    <TiWarningOutline size={32} color={theme.palette.deepLemon} /> :
                                    <TiWarningOutline size={32} color={theme.palette.coralRed} />
                                  }
                                </>
                              }                          
                            </IconButton>
                          </Tooltip>
                          <IconButton>
                            {
                              (item.domain_name && isDomainWithCertificate(item.subdomain_prefix, item.domain_name)) ?
                              <TbCertificate size={32} color={theme.palette.mayGreen} /> :
                              <TbCertificateOff size={32} color={theme.palette.deepLemon} />
                            }
                          </IconButton>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.subdomainPrefixOptional}
                            currentValue={item.subdomain_prefix}
                            setCurrentValue={handleChangeDomain}
                            field_key='subdomain_prefix'
                            index={index}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                          <Box sx={domainPeriodStyle}>.</Box>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.domainName}
                            currentValue={item.domain_name}
                            setCurrentValue={handleChangeDomain}
                            required={true}
                            error={getError('customer-domain_name-' + index)}
                            field_key='domain_name'
                            index={index}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                          {
                            index === 0 ?
                            <IconButton sx={{ color: theme.palette.mayGreen }} onClick={handleAddDomain}>
                              <AddCircleIcon fontSize='large' />
                            </IconButton> :
                            <IconButton sx={{ color: theme.palette.coralRed }} onClick={() => handleRemoveDomain(index)}>
                              <DeleteIcon fontSize='large' />
                            </IconButton>
                          }
                        </Stack>
                      </Box>
                    ))
                  }

                  { width < constants.lgBreakpoint && getDomainVerifyHelper() }
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Box sx={{ margin: width >= constants.smBreakpoint ? '17px 0px 0px 20px' : '10px 0px 0px 0px' }}>
                    <Button
                      variant='text'
                      disableRipple
                      disableElevation
                      onClick={() => verifyDomain({ type: 'client', showLoading: true })}
                      sx={verifyButtonStyle}
                    >
                      {defaultTexts.verify}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Grid container>
                <Grid size={{ xs: 12, lg: 9 }}>
                  <Box sx={{ ...fieldBoxStyle, paddingBottom: '0px' }}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-start'
                        >
                          <Box sx={{ width: '60px' }}>
                            <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.rsaCertificateForDomainHelperText)}>
                              <HelpOutlineIcon fontSize='large' />
                            </IconButton>
                          </Box>
                          <Box sx={sectionLabelStyle}>{defaultTexts.rsaCertificateForCustomerUiDomain}</Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-end'
                        >
                          <Box
                            sx={{
                              ...rsaSwitchStyle,
                              borderRadius: '5px 0px 0px 5px',
                              marginRight: '1px',
                              background: certificateAction === 'upload' ? theme.palette.violetsAreBlue : theme.palette.grayX11,
                            }}
                            onClick={() => handleChangeCertificateAction('upload')}
                          >
                            <Box>{defaultTexts.upload}</Box>
                          </Box>
                          <Box
                            sx={{
                              ...rsaSwitchStyle,
                              borderRadius: '0px 5px 5px 0px',
                              background: certificateAction === 'generate' ? theme.palette.violetsAreBlue : theme.palette.grayX11,
                            }}
                            onClick={() => handleChangeCertificateAction('generate')}
                          >
                            <Box>{defaultTexts.generate}</Box>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>                
                  </Box>
                </Grid>
                <Grid size={{ lg: 3 }}>
                  {
                    certificateAction === 'upload' &&
                    <Box sx={{ margin: '10px 0px 0px 5px' }}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-start'
                      >
                        <IconButton sx={{ color: theme.palette.mayGreen }} onClick={handleAddCertificate}>
                          <AddCircleIcon fontSize='large' />
                        </IconButton>
                        {
                          customerUiCertificates.length > 1 &&
                          <IconButton sx={{ color: theme.palette.coralRed }} onClick={handleRemoveCertificate}>
                            <RemoveCircleIcon fontSize='large' />
                          </IconButton>
                        }
                      </Stack>
                    </Box> 
                  }
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Grid container>
                <Grid size={{ lg: 9 }}>
                  {
                    certificateAction === 'upload' && customerUiCertificates.map((item, index) => (
                      <Box key={index}>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourCertificateHere + '...'}
                              currentValue={item.certificate}
                              setCurrentValue={handleChangeCertificates}
                              required={true}
                              error={getError('certificate-' + index)}
                              field_key='certificate'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourPrivateKeyHere + '...'}
                              currentValue={item.private_key}
                              setCurrentValue={handleChangeCertificates}
                              required={true}
                              error={getError('private_key-' + index)}
                              field_key='private_key'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        <Box sx={fieldBoxStyle}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-start'
                          >
                            <Box sx={{ width: '60px' }}></Box>              
                            <CustomTextField 
                              variant='outlined'
                              label={defaultTexts.copyYourFullChainHere + '...'}
                              currentValue={item.full_chain}
                              setCurrentValue={handleChangeCertificates}
                              error={getError('full_chain-' + index)}
                              field_key='full_chain'
                              index={index}
                              size='small'
                              multiline={true}
                              sx={{ ...formControlStyle, width: '100%' }}
                              outlinedInputSx={outlinedInputStyle}
                            />
                          </Stack>
                        </Box>

                        {
                          index !== customerUiCertificates.length - 1 &&
                          <Box sx={{ ...fieldBoxStyle, marginLeft: '60px' }}>
                            <Divider />
                          </Box>
                        }

                      </Box>
                    ))
                  }
                </Grid>
              </Grid>
            </Box>

          </Grid>
          <Grid size={{ lg: 3 }}>
            { width >= constants.lgBreakpoint && getDomainVerifyHelper() }
          </Grid>
        </Grid>
      </Box>

      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}
      {/* --------------------------------------------------------------------------------- */}

      <Grid container spacing={4}>
        <Grid
          size={{ xs: 12, lg: 7 }}
          sx={{ borderRight: `${theme.palette.lightSilver} 1px dotted` }}
        >

          <Box sx={{ ...fieldBoxStyle, marginTop: '20px' }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}>
                    <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.googleRecaptchaConfigurationHelperText)}>
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Box>              
                  <Box sx={sectionLabelStyle}>{defaultTexts.googleRecaptchaConfiguration}</Box>
                </Stack>
              </Grid>
            </Grid>            
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.siteKey}
                    currentValue={customerUiSiteKey}
                    setCurrentValue={handleChangeSiteKey}
                    error={getError('customerUiSiteKey')}
                    required={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.secretKey}
                    currentValue={customerUiSecretKey}
                    setCurrentValue={handleChangeSecretKey}
                    error={getError('customerUiSecretKey')}
                    required={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Stack>
              </Grid>
            </Grid>            
          </Box>

          <Box sx={{ ...fieldBoxStyle, marginTop: '20px' }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <Box sx={sectionLabelStyle}>{defaultTexts.loginConfiguration}</Box>
                </Stack>
              </Grid>
            </Grid>            
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}>
                    <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.defaultOpenStackDomainHelperText)}>
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Box>              
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.defaultOpenStackDomain}
                    currentValue={defaultOpenStackDomain}
                    setCurrentValue={handleChangeDefaultOpenStackDomain}
                    error={getError('defaultOpenStackDomain')}
                    required={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}>
                    <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.defaultOpenStackUserRoleHelperText)}>
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Box>              
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.defaultOpenStackUserRole}
                    currentValue={defaultOpenStackUserRole}
                    setCurrentValue={handleChangeDefaultOpenStackUserRole}
                    error={getError('defaultOpenStackUserRole')}
                    required={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ ...fieldBoxStyle, marginTop: '40px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={sectionLabelStyle}>
                <CustomCheckboxField
                  label={defaultTexts.signUpEnabled}
                  checked={signUpEnabled}
                  handleChange={handleChangeSignUpEnabled}
                  sx={checkboxStyle}
                  sxLabel={checkboxLabelStyle}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={{ ...fieldBoxStyle }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <Box sx={sectionLabelStyle}>{defaultTexts.registrationFormFields}</Box>
                </Stack>
              </Grid>
            </Grid>            
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomCheckboxField
                    label={defaultTexts.username}
                    checked={isUsername}
                    handleChange={handleChangeIsUsername}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Stack>
              </Grid>
              <Grid size={{ lg: 4 }}>
                <CustomTextField
                  type='number'
                  min={1}
                  variant='outlined'
                  label={defaultTexts.priority}
                  currentValue={usernamePriority}
                  setCurrentValue={handleChangeUsernamePriority}
                  error={getError('usernamePriority')}
                  required={true}
                  size='small'
                  sx={{ ...formControlStyle, width: '100px' }}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomCheckboxField
                    label={defaultTexts.password}
                    checked={isPassword}
                    handleChange={handleChangeIsPassword}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Stack>
              </Grid>
              <Grid size={{ lg: 4 }}>
                <CustomTextField
                  type='number'
                  min={1}
                  variant='outlined'
                  label={defaultTexts.priority}
                  currentValue={passwordPriority}
                  setCurrentValue={handleChangePasswordPriority}
                  error={getError('passwordPriority')}
                  required={true}
                  size='small'
                  sx={{ ...formControlStyle, width: '100px' }}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomCheckboxField
                    label={defaultTexts.confirmPassword}
                    checked={isConfirmPassword}
                    handleChange={handleChangeIsConfirmPassword}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Stack>
              </Grid>
              <Grid size={{ lg: 4 }}>
                <CustomTextField
                  type='number'
                  min={1}
                  variant='outlined'
                  label={defaultTexts.priority}
                  currentValue={confirmPasswordPriority}
                  setCurrentValue={handleChangeConfirmPasswordPriority}
                  error={getError('confirmPasswordPriority')}
                  required={true}
                  size='small'
                  sx={{ ...formControlStyle, width: '100px' }}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Grid container spacing={4}>
              <Grid size={{ lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <CustomCheckboxField
                    label={defaultTexts.email}
                    checked={isEmail}
                    handleChange={handleChangeIsEmail}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Stack>
              </Grid>
              <Grid size={{ lg: 4 }}>
                <CustomTextField
                  type='number'
                  min={1}
                  variant='outlined'
                  label={defaultTexts.priority}
                  currentValue={emailPriority}
                  setCurrentValue={handleChangeEmailPriority}
                  error={getError('emailPriority')}
                  required={true}
                  size='small'
                  sx={{ ...formControlStyle, width: '100px' }}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Box sx={fileMenuStyle}>
              <Box
                component='span'
                sx={termsOfUseType === 'string' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setTermsOfUseType('string')}
              >{defaultTexts.text}</Box>
              <Box component='span'> | </Box>
              <Box
                component='span'
                sx={termsOfUseType === 'file' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setTermsOfUseType('file')}
              >{defaultTexts.uploadFile}</Box>
              <Box component='span'> | </Box>
              <Box
                component='span'
                sx={termsOfUseType === 'url' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setTermsOfUseType('url')}
              >{defaultTexts.fileUrl}</Box>
            </Box>
            <Grid container spacing={4}>
              <Grid size={{ xs:10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box>
                    <CustomCheckboxField
                      label={termsOfUseType !== 'string' && defaultTexts.termsOfUse}
                      checked={isTermsOfUse}
                      handleChange={handleChangeIsTermsOfUse}
                      sx={checkboxStyle}
                      sxLabel={checkboxLabelStyle}
                    />
                    {
                      termsOfUseType === 'file' &&
                      <Box sx={{ marginLeft: '20px' }}>
                        {
                          (termsOfUseFile && typeof termsOfUseFile === 'string') &&
                          <Box sx={openFileStyle} onClick={() => handleOpenFile(termsOfUseFile)}>{getFileName(termsOfUseFile)}</Box>
                        }
                        <Button 
                          component='label'
                          role={undefined}
                          variant='text'
                          tabIndex={-1}
                          startIcon={<FaFileArrowUp size={40} color={theme.palette.davysGrey} />}
                          sx={fileUploadButtonStyle}
                        >
                          { (termsOfUseFile && termsOfUseFile.name) ? termsOfUseFile.name : defaultTexts.uploadFile}
                          <VisuallyHiddenInput type='file' onChange={handleUploadTermsOfUseFile} />
                        </Button>
                      </Box>
                    }

                    {
                      termsOfUseType === 'url' &&
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-start'
                        spacing={1}
                        sx={{ width: '100%' }}
                      >
                        <CustomSelectField
                          items={urlPrefixList} 
                          currentValue={termsOfUseUrlPrefix}
                          setCurrentValue={handleChangeTermsOfUseUrlPrefix}
                          self_item_titles={true}
                          size='small'
                          sx={{ ...formControlStyle, width: '180px' }}
                          outlinedInputSx={outlinedInputStyle}
                        />
                        <CustomTextField
                          variant='outlined'
                          label={defaultTexts.fileUrl}
                          currentValue={termsOfUseUrl}
                          setCurrentValue={handleChangeTermsOfUseUrl}
                          required={true}
                          error={getError('termsOfUseUrl')}
                          size='small'
                          sx={formControlStyle}
                          outlinedInputSx={outlinedInputStyle}
                        />
                      </Stack>
                    }
                  </Box>                  
                  {
                    termsOfUseType === 'string' &&
                    <CustomTextField
                      variant='outlined'
                      label={defaultTexts.termsOfUse}
                      currentValue={termsOfUseText}
                      setCurrentValue={handleChangeTermsOfUseText}
                      required={true}
                      error={getError('termsOfUseText')}
                      size='small'
                      multiline={true}
                      sx={{ ...formControlStyle, width: '70%' }}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  }                  
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Box sx={fileMenuStyle}>
              <Box
                component='span'
                sx={privacyPolicyType === 'string' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setPrivacyPolicyType('string')}
              >{defaultTexts.text}</Box>
              <Box component='span'> | </Box>
              <Box
                component='span'
                sx={privacyPolicyType === 'file' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setPrivacyPolicyType('file')}
              >{defaultTexts.uploadFile}</Box>
              <Box component='span'> | </Box>
              <Box
                component='span'
                sx={privacyPolicyType === 'url' ? fileMenuActiveStyle : fileMenuLinkStyle}
                onClick={() => setPrivacyPolicyType('url')}
              >{defaultTexts.fileUrl}</Box>
            </Box>
            <Grid container spacing={4}>
              <Grid size={{ xs:10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='flex-start'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box>
                    <CustomCheckboxField
                      label={privacyPolicyType !== 'string' && defaultTexts.privacyPolicy}
                      checked={isPrivacyPolicy}
                      handleChange={handleChangeIsPrivacyPolicy}
                      sx={checkboxStyle}
                      sxLabel={checkboxLabelStyle}
                    />
                    {
                      privacyPolicyType === 'file' &&
                      <Box sx={{ marginLeft: '20px' }}>
                        {
                          (privacyPolicyFile && typeof privacyPolicyFile === 'string') &&
                          <Box sx={openFileStyle} onClick={() => handleOpenFile(privacyPolicyFile)}>{getFileName(privacyPolicyFile)}</Box>
                        }
                        <Button 
                          component='label'
                          role={undefined}
                          variant='text'
                          tabIndex={-1}
                          startIcon={<FaFileArrowUp size={40} color={theme.palette.davysGrey} />}
                          sx={fileUploadButtonStyle}
                        >
                          { (privacyPolicyFile && privacyPolicyFile.name) ? privacyPolicyFile.name : defaultTexts.uploadFile}
                          <VisuallyHiddenInput type='file' onChange={handleUploadPrivacyPolicyFile} />
                        </Button>
                      </Box>
                    }

                    {
                      privacyPolicyType === 'url' &&
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-start'
                        spacing={1}
                        sx={{ width: '100%' }}
                      >
                        <CustomSelectField
                          items={urlPrefixList} 
                          currentValue={privacyPolicyUrlPrefix}
                          setCurrentValue={handleChangePrivacyPolicyUrlPrefix}
                          self_item_titles={true}
                          size='small'
                          sx={{ ...formControlStyle, width: '180px' }}
                          outlinedInputSx={outlinedInputStyle}
                        />
                        <CustomTextField
                          variant='outlined'
                          label={defaultTexts.fileUrl}
                          currentValue={privacyPolicyUrl}
                          setCurrentValue={handleChangePrivacyPolicyUrl}
                          required={true}
                          error={getError('privacyPolicyUrl')}
                          size='small'
                          sx={formControlStyle}
                          outlinedInputSx={outlinedInputStyle}
                        />
                      </Stack>
                    }
                  </Box>                  
                  {
                    privacyPolicyType === 'string' &&
                    <CustomTextField
                      variant='outlined'
                      label={defaultTexts.privacyPolicy}
                      currentValue={privacyPolicyText}
                      setCurrentValue={handleChangePrivacyPolicyText}
                      required={true}
                      error={getError('privacyPolicyText')}
                      size='small'
                      multiline={true}
                      sx={{ ...formControlStyle, width: '70%' }}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  }            
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ ...fieldBoxStyle, marginTop: '20px' }}>
            <Grid container spacing={4}>
              <Grid size={{ xs:10, lg: 8 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>              
                  <Box sx={{ ...sectionLabelStyle, marginRight: '10px' }}>{defaultTexts.customFields}</Box>
                  <IconButton sx={{ color: theme.palette.greenSheen }} onClick={handleAddCustomField}>
                    <AddCircleIcon fontSize='large' />
                  </IconButton>
                  <IconButton sx={{ color: theme.palette.coralRed }} onClick={handleRemoveCustomField}>
                    <RemoveCircleIcon fontSize='large' />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {
            customFields.map((item, index) => (
              <Box sx={fieldBoxStyle} key={index}>
                <Grid container spacing={4}>
                  <Grid size={{ lg: 8 }}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='flex-start'
                    >
                      <Box sx={{ width: '60px' }}></Box>
                      <Box sx={{ width: 'calc(90% - 60px)' }}>
                        <Box sx={{ marginBottom: '10px' }}>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.fieldKey}
                            currentValue={item.field_key}
                            field_key='field_key'
                            index={index}
                            setCurrentValue={handleChangeCustomFields}
                            required={true}
                            error={getError('custom-fields-field_key-' + index)}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                        </Box>
                        <Box>
                          <CustomTextField 
                            variant='outlined'
                            label={defaultTexts.label}
                            currentValue={item.label}
                            field_key='label'
                            index={index}
                            setCurrentValue={handleChangeCustomFields}
                            required={true}
                            error={getError('custom-fields-label-' + index)}
                            size='small'
                            sx={formControlStyle}
                            outlinedInputSx={outlinedInputStyle}
                          />
                        </Box>
                      </Box>                      
                    </Stack>
                  </Grid>
                  <Grid size={{ lg: 4 }}>
                    <CustomTextField
                      type='number'
                      min={1}
                      variant='outlined'
                      label={defaultTexts.priority}
                      currentValue={item.priority}
                      field_key='priority'
                      index={index}
                      setCurrentValue={handleChangeCustomFields}
                      required={true}
                      error={getError('custom-fields-priority-' + index)}
                      size='small'
                      sx={{ ...formControlStyle, width: '100px' }}
                      outlinedInputSx={outlinedInputStyle}
                    />
                    <Box sx={{ marginTop: '10px' }}>
                      <CustomSwitchField
                        label={defaultTexts.required}
                        checked={item.required}
                        field_key='required'
                        index={index}
                        handleChange={handleChangeCustomFields}
                        sxLabel={requiredSwitchLabelStyle}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))
          }

        </Grid>

        {/* --------------------------------------------------------------------------------- */}
        {/* --------------------------------------------------------------------------------- */}
        {/* --------------------------------------------------------------------------------- */}
        {/* --------------------------------------------------------------------------------- */}
        {/* --------------------------------------------------------------------------------- */}

        <Grid size={{ xs: 12, lg: 5 }}>

          <Box sx={appearanceLabelStyle}>{defaultTexts.customizeYourInterfaceAppearance}</Box>

          {
            (customerUiLogo && typeof customerUiLogo === 'string') &&
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{
                borderBottom: `${theme.palette.lightSilver} 1px dotted`,
                paddingBottom: '10px',
                marginBottom: '20px',
              }}
            >
              <Box>
                <img src={constants.s3URL + constants.s3Bucket + customerUiLogo} alt='' style={{ maxWidth: '200px' }} />
              </Box>
              <Box sx={valueMdStyle}>{defaultTexts.customerUiLogo}</Box>
            </Stack>
          }

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{
              borderBottom: `${theme.palette.lightSilver} 1px solid`,
              paddingBottom: '20px',
              marginBottom: '20px',
            }}
          >
            <Button 
              component='label'
              role={undefined}
              variant='contained'
              tabIndex={-1}
              sx={uploadButtonStyle}
            >
              {defaultTexts.logo}
              <VisuallyHiddenInput type='file' onChange={handleUploadLogo} />
            </Button>
            <Box sx={uploadLogoDescriptionStyle}>{ customerUiLogo ? customerUiLogo.name : defaultTexts.uploadLogosFile }</Box>
          </Stack>

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{ marginBottom: '20px' }}
          >
            <Box
              sx={{ ...pickColorStyle, background: customerUiPrimaryColor }}
              onClick={handlePrimaryColorDialogOpen}
            ></Box>
            <Box sx={uploadLogoDescriptionStyle}>{defaultTexts.primaryColor}</Box>
          </Stack>

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            spacing={2}
            sx={{ marginBottom: '20px' }}
          >
            <Box
              sx={{ ...pickColorStyle, background: customerUiSecondaryColor }}
              onClick={handleSecondaryColorDialogOpen}
            ></Box>
            <Box sx={uploadLogoDescriptionStyle}>{defaultTexts.secondaryColor}</Box>
          </Stack>

          <Box sx={{ ...fieldBoxStyle, marginTop: '40px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={sectionLabelStyle}>{defaultTexts.customizeStartPage}</Box>
            </Stack>           
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(100% - 120px)' }}>
                <CustomSelectField
                  label={defaultTexts.backgroundType}
                  items={backgroundTypeList} 
                  currentValue={backgroundType}
                  setCurrentValue={handleChangeBackgroundType}
                  required={true}
                  error={getError('backgroundType')}
                  self_item_titles={true}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>
            </Stack>           
          </Box>

          {
            backgroundType === 'color' &&
            <Box sx={fieldBoxStyle}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
              >
                <Box sx={{ width: '60px' }}></Box>
                <Box sx={{ width: 'calc(100% - 120px)' }}>
                  <CustomSelectField
                    label={defaultTexts.colorType}
                    items={colorTypeList} 
                    currentValue={colorType}
                    setCurrentValue={handleChangeColorType}
                    required={true}
                    error={getError('colorType')}
                    self_item_titles={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Box>
              </Stack>
            </Box>
          }

          {
            (backgroundType === 'color' && colorType === 'solid') &&
            <Box sx={fieldBoxStyle}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
              >
                <Box sx={{ width: '60px' }}></Box>
                <Box sx={{ width: 'calc(100% - 120px)', marginRight: '10px' }}>
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.backgroundColor}
                    currentValue={backgroundColor}
                    setCurrentValue={handleChangeBackgroundColor}
                    required={true}
                    error={getError('backgroundColor')}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Box>
                <Box
                  sx={{ ...colorPickerIconSmallStyle, background: backgroundColor }}
                  onClick={handleBackgroundColorDialogOpen}
                ></Box>
              </Stack>
            </Box>
          }

          {
            (backgroundType === 'color' && colorType === 'gradient') &&
            <>
              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(100% - 120px)', marginRight: '10px' }}>
                    <CustomTextField 
                      variant='outlined'
                      label={defaultTexts.gradientColor + ' 1'}
                      currentValue={gradientColor1}
                      setCurrentValue={handleChangeGradientColor1}
                      required={true}
                      error={getError('gradientColor1')}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>
                  <Box
                    sx={{ ...colorPickerIconSmallStyle, background: gradientColor1 }}
                    onClick={handleGradientColor1DialogOpen}
                  ></Box>
                </Stack>
              </Box>

              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(100% - 120px)', marginRight: '10px' }}>
                    <CustomTextField 
                      variant='outlined'
                      label={defaultTexts.gradientColor + ' 2'}
                      currentValue={gradientColor2}
                      setCurrentValue={handleChangeGradientColor2}
                      required={true}
                      error={getError('gradientColor2')}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>
                  <Box
                    sx={{ ...colorPickerIconSmallStyle, background: gradientColor2 }}
                    onClick={handleGradientColor2DialogOpen}
                  ></Box>
                </Stack>
              </Box>
            </>
          }

          {
            backgroundType === 'image' &&
            <Box sx={fieldBoxStyle}>
              {
                (backgroundImage && typeof backgroundImage === 'string') &&
                <Box sx={{ marginBottom: '10px' }}>
                  <img src={constants.s3URL + constants.s3Bucket + backgroundImage} alt='' style={{ maxWidth: '200px' }} />
                </Box>
              }
              <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Button 
                    component='label'
                    role={undefined}
                    variant='text'
                    tabIndex={-1}
                    startIcon={<FaImage size={32} color={theme.palette.davysGrey} />}
                    sx={imageUploadButtonStyle}
                  >
                    { (backgroundImage && backgroundImage.name) ? backgroundImage.name : defaultTexts.uploadBackgroundImage}
                    <VisuallyHiddenInput type='file' onChange={handleUploadBackgroundImageFile} />
                  </Button>
                </Stack>
            </Box>
          }

          <Box sx={{ ...fieldBoxStyle, marginTop: '20px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(100% - 120px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.headingText}
                  currentValue={headingText}
                  setCurrentValue={handleChangeHeadingText}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(100% - 120px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.promoText + ' 1'}
                  currentValue={promoText1}
                  setCurrentValue={handleChangePromoText1}
                  multiline={true}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(100% - 120px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.promoText + ' 2'}
                  currentValue={promoText2}
                  setCurrentValue={handleChangePromoText2}
                  multiline={true}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(100% - 120px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.promoText + ' 3'}
                  currentValue={promoText3}
                  setCurrentValue={handleChangePromoText3}
                  multiline={true}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={{ ...fieldBoxStyle, marginTop: '40px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={sectionLabelStyle}>
                <CustomCheckboxField
                  label={defaultTexts.customizeContactInfo}
                  checked={isContact}
                  handleChange={handleChangeIsContact}
                  sx={checkboxStyle}
                  sxLabel={checkboxLabelStyle}
                />
              </Box>
            </Stack>
          </Box>

          {
            isContact &&
            <>
              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(100% - 120px)' }}>
                    <CustomTextField 
                      variant='outlined'
                      label={defaultTexts.company}
                      currentValue={contactCompany}
                      setCurrentValue={handleChangeContactCompany}
                      required={true}
                      error={getError('contactCompany')}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>
                </Stack>
              </Box>

              {
                contactPhone.map((item, index) => (
                  <Box sx={fieldBoxStyle} key={index}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='flex-start'
                    >
                      <Box sx={{ width: '60px' }}>
                        {
                          index === 0 ?
                          <IconButton sx={{ color: theme.palette.greenSheen }} onClick={handleAddContactPhone}>
                            <AddCircleIcon fontSize='large' />
                          </IconButton> :
                          <IconButton sx={{ color: theme.palette.coralRed }} onClick={() => handleRemoveContactPhone(index)}>
                            <RemoveCircleIcon fontSize='large' />
                          </IconButton>
                        }
                      </Box>
                      <Box sx={{ width: 'calc(100% - 120px)' }}>
                        <CustomTextField 
                          variant='outlined'
                          label={defaultTexts.phone + ' ' + (index + 1)}
                          currentValue={item}
                          index={index}
                          setCurrentValue={handleChangeContactPhone}
                          required={index === 0}
                          error={index === 0 ? getError('contactPhone') : null}
                          size='small'
                          sx={formControlStyle}
                          outlinedInputSx={outlinedInputStyle}
                        />
                      </Box>
                    </Stack>
                  </Box>
                ))
              }

              {
                contactEmail.map((item, index) => (
                  <Box sx={fieldBoxStyle} key={index}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='flex-start'
                    >
                      <Box sx={{ width: '60px' }}>
                        {
                          index === 0 ?
                          <IconButton sx={{ color: theme.palette.greenSheen }} onClick={handleAddContactEmail}>
                            <AddCircleIcon fontSize='large' />
                          </IconButton> :
                          <IconButton sx={{ color: theme.palette.coralRed }} onClick={() => handleRemoveContactEmail(index)}>
                            <RemoveCircleIcon fontSize='large' />
                          </IconButton>
                        }
                      </Box>
                      <Box sx={{ width: 'calc(100% - 120px)' }}>
                        <CustomTextField 
                          variant='outlined'
                          label={defaultTexts.email + ' ' + (index + 1)}
                          currentValue={item}
                          index={index}
                          setCurrentValue={handleChangeContactEmail}
                          required={index === 0}
                          error={index === 0 ? getError('contactEmail') : null}
                          size='small'
                          sx={formControlStyle}
                          outlinedInputSx={outlinedInputStyle}
                        />
                      </Box>
                    </Stack>
                  </Box>
                ))
              }
            </>
          }

        </Grid>
      </Grid>

      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{
          text: helperText,
        }}
      />

      <CustomDialog
        open={primaryColorDialogOpen}
        onClose={handlePrimaryColorDialogClose}
        dialogTitle={{ title: defaultTexts.primaryColor }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handlePrimaryColorDialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={customerUiPrimaryColor}
          setColor={setCustomerUiPrimaryColor}
        />
      </CustomDialog>

      <CustomDialog
        open={secondaryColorDialogOpen}
        onClose={handleSecondaryColorDialogClose}
        dialogTitle={{ title: defaultTexts.secondaryColor }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handleSecondaryColorDialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={customerUiSecondaryColor}
          setColor={setCustomerUiSecondaryColor}
        />
      </CustomDialog>

      <CustomDialog
        open={backgroundColorDialogOpen}
        onClose={handleBackgroundColorDialogClose}
        dialogTitle={{ title: defaultTexts.backgroundColor }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handleBackgroundColorDialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={backgroundColor}
          setColor={setBackgroundColor}
        />
      </CustomDialog>

      <CustomDialog
        open={gradientColor1DialogOpen}
        onClose={handleGradientColor1DialogClose}
        dialogTitle={{ title: defaultTexts.gradientColor + ' 1' }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handleGradientColor1DialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={gradientColor1}
          setColor={setGradientColor1}
        />
      </CustomDialog>

      <CustomDialog
        open={gradientColor2DialogOpen}
        onClose={handleGradientColor2DialogClose}
        dialogTitle={{ title: defaultTexts.gradientColor + ' 2' }}
        actionButtons={[{ title: defaultTexts.apply, onClick: handleGradientColor2DialogClose }]}
        maxWidth='xs'
      >
        <CustomColorPicker
          color={gradientColor2}
          setColor={setGradientColor2}
        />
      </CustomDialog>
    </>
  )
}

export default CustomerUiEdit